<template>
  <div class="fill-height d-flex flex-column justify-space-between ga-6">
    <div>
      <v-row>
        <v-col cols="12" sm="6" md="4" class="d-flex flex-column ga-6">
          <SummarySection
            :title="t('onboarding.site.review.site_details_section_label')"
            :items="[
              { label: t('onboarding.site.review.site_name_label'), value: site?.name },
              {
                label: t('onboarding.site.review.site_display_name_label'),
                value: site?.displayName,
              },
              {
                label: t('onboarding.site.review.site_address_label'),
                value: site?.address?.line1,
              },
              { label: '', value: site?.address?.line2 },
              { label: '', value: site?.address?.city },
              { label: '', value: site?.address?.postalCode },
            ]"
            :can-edit="canEdit"
            @go-to="() => emit('goTo', 'siteName')"
          />
          <SummarySection
            v-if="smAndUp"
            :title="t('onboarding.site.review.features_section_label')"
            :items="[
              { label: t('onboarding.site.review.features_tipping_label'), value: tipProvider },
              {
                label: t('onboarding.site.review.features_pay_by_link_label'),
                value: site?.enablePBL
                  ? t('onboarding.site.review.features_pay_by_link_requested')
                  : t('onboarding.site.review.features_pay_by_link_not_requested'),
              },
              {
                label: t('onboarding.site.review.features_moto_label'),
                value: site?.enableMOTO
                  ? t('onboarding.site.review.features_moto_requested')
                  : t('onboarding.site.review.features_moto_not_requested'),
              },
            ]"
            :can-edit="canEdit"
            @go-to="() => emit('goTo', 'features')"
          />
          <SummarySection
            v-if="sm"
            :title="t('onboarding.site.review.payments_section_label')"
            :items="[
              { label: t('onboarding.site.review.payment_methods_label'), value: paymentMethods },
            ]"
            :can-edit="canEdit"
            @go-to="() => emit('goTo', 'paymentMethods')"
          />
        </v-col>
        <v-col v-if="xs" cols="12" sm="6" md="4">
          <SummarySection
            :title="t('onboarding.site.review.features_section_label')"
            :items="[
              { label: t('onboarding.site.review.features_tipping_label'), value: tipProvider },
              {
                label: t('onboarding.site.review.features_pay_by_link_label'),
                value: site?.enablePBL
                  ? t('onboarding.site.review.features_pay_by_link_requested')
                  : t('onboarding.site.review.features_pay_by_link_not_requested'),
              },
              {
                label: t('onboarding.site.review.features_moto_label'),
                value: site?.enableMOTO
                  ? t('onboarding.site.review.features_moto_requested')
                  : t('onboarding.site.review.features_moto_not_requested'),
              },
            ]"
            :can-edit="canEdit"
            @go-to="() => emit('goTo', 'features')"
          />
        </v-col>
        <v-col v-if="!sm" cols="12" sm="6" md="4" class="d-flex flex-column ga-6">
          <SummarySection
            :title="t('onboarding.site.review.payments_section_label')"
            :items="[
              { label: t('onboarding.site.review.payment_methods_label'), value: paymentMethods },
            ]"
            :can-edit="canEdit"
            @go-to="() => emit('goTo', 'paymentMethods')"
          />
          <DevicesSummarySection
            v-if="mdAndUp"
            :device-basket="deviceBasket"
            :can-edit="canEdit"
            @go-to="() => emit('goTo', 'devices')"
          />
          <SummarySection
            v-if="!!billingPaymentMethod"
            :title="t('onboarding.site.review.billing_payments_section_label')"
            :items="[
              {
                label: t('onboarding.site.review.billing_payments_methods_label'),
                value: t(
                  `onboarding.site.review.billing_payments_methods_${billingPaymentMethod?.type ?? 'unknown'}`,
                ),
              },
              {
                label: t('onboarding.site.review.billing_payments_methods_last4_label'),
                value: `***${billingPaymentMethod?.last4}`,
              },
            ]"
          />
        </v-col>
        <v-col v-if="xs" cols="12" sm="6" md="4">
          <DevicesSummarySection
            :device-basket="deviceBasket"
            :can-edit="canEdit"
            @go-to="() => emit('goTo', 'devices')"
          />
        </v-col>
        <v-col v-if="xs" cols="12" sm="6" md="4">
          <SummarySection
            :title="t('onboarding.site.review.delivery_poc_section_label')"
            :items="[
              {
                label: t('onboarding.site.review.poc_name_label'),
                value: site?.pointOfContact?.name,
              },
              {
                label: t('onboarding.site.review.poc_email_label'),
                value: site?.pointOfContact?.email,
                truncate: true,
              },
              {
                label: t('onboarding.site.review.poc_phone_label'),
                value: site?.pointOfContact?.phoneNumber,
              },
              {
                label: t('onboarding.site.review.poc_role_label'),
                value: site?.pointOfContact?.role,
              },
              {
                label: t('onboarding.site.review.delivery_address_label'),
                value: site?.deliveryAddress?.line1,
              },
              { label: '', value: site?.deliveryAddress?.line2 },
              { label: '', value: site?.deliveryAddress?.city },
              { label: '', value: site?.deliveryAddress?.postalCode },
              {
                label: t('onboarding.site.review.delivery_notes_label'),
                value: site?.deliveryNotes,
              },
            ]"
            :can-edit="canEdit"
            @go-to="() => emit('goTo', 'siteAddress')"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" class="d-flex flex-column ga-6">
          <DevicesSummarySection
            v-if="sm"
            :device-basket="deviceBasket"
            :can-edit="canEdit"
            @go-to="() => emit('goTo', 'devices')"
          />
          <SummarySection
            v-if="smAndUp"
            :title="t('onboarding.site.review.delivery_poc_section_label')"
            :items="[
              {
                label: t('onboarding.site.review.poc_name_label'),
                value: site?.pointOfContact?.name,
              },
              {
                label: t('onboarding.site.review.poc_email_label'),
                value: site?.pointOfContact?.email,
              },
              {
                label: t('onboarding.site.review.poc_phone_label'),
                value: site?.pointOfContact?.phoneNumber,
              },
              {
                label: t('onboarding.site.review.poc_role_label'),
                value: site?.pointOfContact?.role,
              },
              {
                label: t('onboarding.site.review.delivery_address_label'),
                value: site?.deliveryAddress?.line1,
              },
              { label: '', value: site?.deliveryAddress?.line2 },
              { label: '', value: site?.deliveryAddress?.city },
              { label: '', value: site?.deliveryAddress?.postalCode },
            ]"
            :can-edit="canEdit"
            @go-to="() => emit('goTo', 'siteAddress')"
          />
          <div>
            <p class="text-grey text-body-2 font-weight-regular text-uppercase mx-2 mb-2">
              {{ t("onboarding.site.review.device_terms_section_label") }}
            </p>
            <div class="py-7 px-6 px-lg-11 rounded-xl elevation-1 bg-background">
              <v-checkbox v-model="termsAccepted" hide-details>
                <template #label>
                  <span>
                    {{ t("onboarding.site.review.device_rental_terms_intro") }}
                    <v-tooltip location="bottom">
                      <template #activator="{ props: _props }">
                        <a
                          target="_blank"
                          href="https://yetipay.me/terms"
                          v-bind="_props"
                          @click.stop
                          >{{ t("onboarding.site.review.device_rental_terms_link") }}</a
                        >
                      </template>
                      {{ t("onboarding.site.review.device_rental_terms_tooltip") }}</v-tooltip
                    >
                  </span>
                </template>
              </v-checkbox>
              <p
                class="text-error text-caption ps-3"
                :style="termsAccepted ? 'visibility: hidden;' : ''"
              >
                {{ t("onboarding.site.review.device_rental_terms_validation_error") }}
              </p>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DraftSite } from "@tableyeti/merchant-service";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { useOnboardingStore } from "@/store/onboarding";
import { tradedPaymentMethods } from "@/utils/config";

const { t } = useI18n();
const { xs, sm, smAndUp, mdAndUp } = useDisplay();
const onboardingStore = useOnboardingStore();

const props = defineProps<{
  canEdit?: boolean;
  site?: DraftSite;
  deviceBasket: Record<string, number>;
}>();
const termsAccepted = defineModel<boolean>({
  required: true,
});
const emit = defineEmits<{
  goTo: [string];
}>();

const paymentMethods = computed(() =>
  (
    props.site?.paymentMethods
      ?.map((pm) => tradedPaymentMethods.find((tpm) => tpm.code === pm)?.name)
      .filter((pm) => !!pm) ?? []
  ).join(", "),
);

const billingPaymentMethod = onboardingStore?.draftMerchant?.site?.billingPaymentMethod;

const tipProvider = computed(() => {
  switch (props.site?.tips?.provider) {
    case "tipjar":
      return "TiPJAR";
    case "yetipay":
      return "yetipay";
    default:
      return "Not enabled";
  }
});
</script>
